import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { smartSchedulingService, preferenceLearningService } from '../../services/api';

function localToday(timeZone) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const value = (type) => parts.find((part) => part.type === type).value;
  return `${value('year')}-${value('month')}-${value('day')}`;
}

export default function CalendarAvailability() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const timezone = user?.timezone || 'UTC';
  const [form, setForm] = useState(() => ({ start_date: localToday(timezone), end_date: localToday(timezone), local_start: '09:00', local_end: '18:00', duration_minutes: '', weekdays: [0, 1, 2, 3, 4, 5, 6] }));
  const [title, setTitle] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [preferenceNotice, setPreferenceNotice] = useState('');
  const generation = useRef(0);
  useEffect(() => () => { generation.current += 1; }, []);
  useEffect(() => { generation.current += 1; setResult(null); setLoading(false); setLoadingPreferences(false); setPreferenceNotice(''); }, [timezone]);

  const change = (key, value) => {
    generation.current += 1;
    setForm((previous) => ({ ...previous, [key]: value }));
    setResult(null); setError(''); setLoading(false); setLoadingPreferences(false); setPreferenceNotice('');
  };
  const loadPreferences = async () => {
    const current = ++generation.current;
    setLoadingPreferences(true); setError(''); setPreferenceNotice(''); setResult(null); setLoading(false);
    try {
      const profile = await preferenceLearningService.getProfile();
      if (current !== generation.current) return;
      const saved = profile.personalization;
      if (!saved.is_saved || saved.invalid_saved_values || (!saved.values.planning_window && !saved.values.meeting_minutes)) {
        setPreferenceNotice('No valid calendar defaults saved. Set them in Profile or enter this search window here.');
      } else {
        setForm((previous) => ({...previous, ...(saved.values.planning_window || {}),
          duration_minutes:saved.values.meeting_minutes ?? previous.duration_minutes}));
        setPreferenceNotice('Saved calendar defaults loaded. Review the dates and window before searching.');
      }
    } catch { if (current === generation.current) setError('Could not load saved calendar defaults. Your entered window was kept.'); }
    finally { if (current === generation.current) setLoadingPreferences(false); }
  };
  const search = async (event) => {
    event.preventDefault();
    const current = ++generation.current;
    setLoading(true); setResult(null); setError('');
    try {
      const [availability, insights] = await Promise.all([
        smartSchedulingService.findTime({ ...form, duration_minutes: Number(form.duration_minutes) }),
        smartSchedulingService.getInsights({ start_date: form.start_date, end_date: form.end_date }),
      ]);
      if (current === generation.current) setResult({ ...availability, insights });
    } catch (failure) {
      if (current === generation.current) {
        const detail = failure.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Could not check this window. Use dates within 31 days, an end time after the start, and at least one weekday.');
      }
    } finally {
      if (current === generation.current) setLoading(false);
    }
  };
  const review = (slot) => navigate('/ai', { state: { reviewFirst: true,
    prompt: `${title.trim() ? `Prepare a calendar event titled ${JSON.stringify(title.trim())}` : 'Help me plan a calendar event; ask me for its title'} from ${slot.start_time} to ${slot.end_time} (${result.timezone}). Recheck my saved calendar before preparing the plan. This time has not been reserved.` } });
  const clock = (value) => new Intl.DateTimeFormat(undefined, { timeZone: result.timezone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(new Date(value));

  return <section className="surface-panel mb-4 p-4" aria-labelledby="find-time-title">
    <h2 id="find-time-title" className="text-base font-bold">Find time in your calendar</h2>
    <p className="mt-1 text-sm text-[#65716a]">Choose a window in {timezone}. Uses saved events; other people’s availability and external changes are not checked.</p>
    <button type="button" className="secondary-button mt-3" disabled={loadingPreferences || loading} onClick={loadPreferences}>{loadingPreferences ? 'Loading saved defaults…' : 'Use saved calendar defaults'}</button>
    {preferenceNotice && <p role="status" className="mt-2 text-sm">{preferenceNotice}</p>}
    <form onSubmit={search} className="mt-3">
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          ['start_date', 'From date', 'date'], ['end_date', 'Through date', 'date'],
          ['local_start', 'Earliest start', 'time'], ['local_end', 'Latest finish', 'time'],
          ['duration_minutes', 'Minutes needed', 'number'],
        ].map(([key, label, type]) => <label key={key} className="text-sm font-medium">{label}
          <input className="mt-1 w-full rounded border border-[#d8dfda] p-2" required type={type} value={form[key]}
            min={type === 'number' ? 5 : undefined} max={type === 'number' ? 720 : undefined}
            onChange={(event) => change(key, event.target.value)} />
        </label>)}
        <button className="primary-button self-end" disabled={loading || loadingPreferences}>{loading ? 'Checking…' : 'Find available times'}</button>
      </div>
      <fieldset className="mt-3 flex flex-wrap gap-3"><legend className="mb-1 text-sm font-medium">Include days</legend>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => <label key={day} className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={form.weekdays.includes(index)} onChange={() => change('weekdays', form.weekdays.includes(index) ? form.weekdays.filter((value) => value !== index) : [...form.weekdays, index])} />{day}
        </label>)}
      </fieldset>
    </form>
    {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
    {result && <div className="mt-4 border-t border-[#e2e7e3] pt-3" aria-live="polite">
      <p className="text-sm">{result.insights.busy_event_count} saved busy events · {result.insights.booked_minutes} occupied minutes across the selected dates{!result.insights.coverage.complete ? ' (incomplete calendar data)' : ''}.</p>
      <p className="mt-1 text-xs text-[#65716a]">{result.method} Checked {clock(result.checked_at)}.</p>
      {result.coverage.connected_events > 0 && <p className="mt-1 text-xs text-[#65716a]">Connected events are cached{result.coverage.oldest_observation ? `; oldest observation ${clock(result.coverage.oldest_observation)}` : ''}{result.coverage.missing_observation_count > 0 ? '; some refresh times are unknown' : ''}. Sync connected calendars before deciding.</p>}
      {!result.coverage.complete ? <p role="alert" className="mt-3 text-sm text-amber-800">Availability is incomplete. Refresh unresolved calendar occurrences or narrow the date window before choosing a time.</p>
        : result.slots.length === 0 ? <p className="mt-3 text-sm">No future slot fits this duration and window in your saved calendar. Try another window.</p>
          : <><label className="mt-3 block text-sm font-medium">Event title (optional)<input className="ml-2 rounded border border-[#d8dfda] p-2" maxLength={240} value={title} onChange={(event) => setTitle(event.target.value)} /></label>
            <ul className="mt-3 grid gap-2 md:grid-cols-2">{result.slots.map((slot) => <li key={slot.start_time} className="flex flex-wrap items-center justify-between gap-2 rounded border border-[#e2e7e3] p-3">
              <span className="text-sm">{clock(slot.start_time)} – {clock(slot.end_time)}</span>
              <button className="secondary-button" onClick={() => review(slot)}>Plan this time</button>
            </li>)}</ul>
            {result.more_available && <p className="mt-2 text-xs">Showing the first {result.slots.length} gaps. Narrow the dates to see later options.</p>}
            <p className="mt-2 text-xs text-[#65716a]">Planning opens the assistant in review mode. AI configuration is required to prepare a plan; no event is created by this search.</p>
          </>}
    </div>}
  </section>;
}
